import React from 'react';

import { Colors, Text } from '@revenuemarket/cds';
import { overlays } from '@revenuemarket/overlays';
import { SystemTime } from '@revenuemarket/ui';
import { mixpanelTrack } from '@revenuemarket/utils';
import {
  Button,
  Flex,
  IconArrowRightLine,
  IconCheckCircleFill,
  IconLoadingLine,
  overlay,
} from '@revenuemarket/vds';
import { useRouter } from 'next/router';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';

import { CardConnectionDrawer } from '../Drawer/CardDrawer';
import ScrapeStatusDrawer from '../layout/ScrapeStatusDrawer';
import CertificateRegisterModal from '../Modal/CertificateRegisterModal';
import {
  ListHeaderWrapper,
  ListItemLeft,
  ListItemRight,
  ListItemTop,
  ListItemWrapper,
  ListWrapper,
  ProgressBarFill,
  ProgressBarShimmer,
  ProgressBarWrapper,
} from './style';
import SwipeText from './SwipeText';
import { SCRAPE_STATUS_ITEM_TYPE, ScrapeStatusItem } from './types';

interface ScrapeFloatingPanelListProps {
  ScrapeStatusItem: ScrapeStatusItem;
  inProgressLabels: string[];
}

const ScrapeFloatingPanelList: React.FC<ScrapeFloatingPanelListProps> = ({
  ScrapeStatusItem,
  inProgressLabels,
}) => {
  const router = useRouter();

  const getStatusText = (item: ScrapeStatusItem[keyof ScrapeStatusItem]) => {
    if (item.status === 'success') return '완료';
    if (item.status === 'error') return '오류';
    if (item.status === 'notConnected') return '미연동';
    return `${item.completedCount}/${item.totalCount}`;
  };

  const getOrganizationType = (scrapeType: string): string => {
    switch (scrapeType) {
      case SCRAPE_STATUS_ITEM_TYPE.BANK:
        return 'bank';
      case SCRAPE_STATUS_ITEM_TYPE.HOMETAX:
        return 'homeTax';
      case SCRAPE_STATUS_ITEM_TYPE.CARD:
        return 'card';
      default:
        return 'bank';
    }
  };

  const handleNavigateToDataManage = (scrapeType: string) => {
    const organizationType = getOrganizationType(scrapeType);
    router.push(`/clobe/data-manage?type=${organizationType}`);
  };

  const handleClickDetail = () => {
    mixpanelTrack(MIXPANEL_EVENT.SCRAPE_FLOATING_PANEL.CLICK_DETAIL);
    overlay.open(({ isOpen, close }) => <ScrapeStatusDrawer isOpen={isOpen} onClose={close} />);
  };

  const handleOpenCertificateModal = () => {
    overlays.open(({ isOpen, onClose }) => (
      <CertificateRegisterModal open={isOpen} onCancel={onClose} />
    ));
  };

  const handleOpenCardConnectionDrawer = () => {
    overlays.open(({ isOpen, onClose }) => (
      <CardConnectionDrawer isOpen={isOpen} onClose={onClose} />
    ));
  };

  const statusList = Object.entries(ScrapeStatusItem)
    .filter(([key]) => key !== SCRAPE_STATUS_ITEM_TYPE.TOTAL)
    .map(([key, item]) => ({ ...item, key }));

  return (
    <Flex direction="column" style={{ flex: 1 }}>
      <ListHeaderWrapper>
        <Flex direction="column">
          <Text textStyle="10_500" style={{ color: Colors.whiteOpacity52 }}>
            전체 진행률
          </Text>
          <SwipeText textStyle="13_600" labels={inProgressLabels} swipeInterval={5000} />
        </Flex>
        <Text textStyle="24_600" style={{ color: Colors.white }}>
          {ScrapeStatusItem[SCRAPE_STATUS_ITEM_TYPE.TOTAL].progressPercent}%
        </Text>
      </ListHeaderWrapper>
      <ListWrapper>
        {statusList.map((item) => (
          <ListItemWrapper key={item.label}>
            <ListItemTop>
              <ListItemLeft>
                <Text textStyle="13_600" style={{ color: Colors.gray900 }}>
                  {item.label}
                </Text>
                {item.status === 'inProgress' && (
                  <IconLoadingLine width={16} height={16} fill={Colors.blue600} spin />
                )}
                {item.status === 'success' && (
                  <IconCheckCircleFill width={16} height={16} fill={Colors.green400} />
                )}
                {item.status === 'error' && (
                  <SystemTime width={16} height={16} color={Colors.red500} />
                )}
                {item.status === 'notConnected' && (
                  <IconArrowRightLine
                    width={16}
                    height={16}
                    fill={Colors.gray400}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleNavigateToDataManage(item.key)}
                  />
                )}
              </ListItemLeft>
              <ListItemRight>
                {item.status === 'notConnected' ? (
                  <Text
                    textStyle="14_600"
                    style={{ color: Colors.gray400, cursor: 'pointer' }}
                    onClick={
                      item.key === SCRAPE_STATUS_ITEM_TYPE.CARD
                        ? handleOpenCardConnectionDrawer
                        : handleOpenCertificateModal
                    }
                  >
                    미연동
                  </Text>
                ) : (
                  <Text
                    textStyle="14_600"
                    style={{
                      color:
                        item.status === 'success'
                          ? Colors.green400
                          : item.status === 'error'
                            ? Colors.red500
                            : Colors.gray600,
                    }}
                  >
                    {item.status === 'inProgress'
                      ? `${item.progressPercent}%`
                      : getStatusText(item)}
                  </Text>
                )}
              </ListItemRight>
            </ListItemTop>
            <ProgressBarWrapper>
              <ProgressBarFill progress={item.progressPercent} status={item.status} />
              {item.status === 'inProgress' && <ProgressBarShimmer />}
            </ProgressBarWrapper>
          </ListItemWrapper>
        ))}
      </ListWrapper>
      <Flex justify="center" align="center" style={{ padding: '8px 16px' }}>
        <Button buttonType="normal" width="100%" onClick={handleClickDetail}>
          상세 내역 보기
        </Button>
      </Flex>
    </Flex>
  );
};

export default ScrapeFloatingPanelList;
